if (typeof CartGift !== 'function') {

    class CartGift extends HTMLElement {

        constructor() {
            super();
            // Initialize dependentCheckboxes as instance property
            this.dependentCheckboxes = [];
            this.giftProductId = this.dataset.giftProductVariantId;
            this.giftBoxProductId = this.dataset.giftBoxProductVariantId;
            
            // Get context from data attribute (defaults to 'default' if not specified)
            this.context = this.dataset.context || 'default';

            // Initialize DOM elements
            this.initializeDOMElements();

            this.init();
        }

        initializeDOMElements() {
            // Create context-specific selectors
            const suffix = this.context !== 'default' ? `-${this.context}` : '';
            
            // Find elements with context-specific IDs, fallback to original IDs for backward compatibility
            this.giftbagCheckbox = document.getElementById(`giftbag-checkbox${suffix}`) || 
                                  document.getElementById('giftbag-checkbox');
            this.giftboxCheckbox = document.getElementById(`giftbox-checkbox${suffix}`) || 
                                  document.getElementById('giftbox-checkbox');
            this.cartDrawerMessage = document.getElementById(`cart-message${suffix}`) || 
                                    document.getElementById('cart-message');
            this.giftBoxCartMessage = document.getElementById(`giftbox-cart-message${suffix}`) || 
                                     document.getElementById('giftbox-cart-message');
            this.messageToInput = document.getElementById(`message-to${suffix}`) || 
                                 document.getElementById('message-to');
            this.messageFromInput = document.getElementById(`message-from${suffix}`) || 
                                   document.getElementById('message-from');
            this.messageTextInput = document.getElementById(`message-text${suffix}`) || 
                                   document.getElementById('message-text');
            this.messageStatus = document.getElementById(`message-status${suffix}`) || 
                                document.getElementById('message-status');
            this.addMessageBtn = document.getElementById(`add-message-btn${suffix}`) || 
                                document.getElementById('add-message-btn');
            
            // These elements might be within this component or global
            this.personalizedMessageContainer = this.querySelector('.personalized-message-container') || 
                                               document.querySelector('.personalized-message-container');
            this.cartTotalPrice = document.getElementById('cart-total-price');
        }

        init() {
            console.log(`CartGift initialized for context: ${this.context}`);
            
            // Get the main checkbox element (try context-specific class first)
            const giftCheckbox = this.querySelector(`.gift_enables_${this.context}`) || 
                                this.querySelector('.gift_enables_drawer') ||
                                document.querySelector('.gift_enables_drawer');

            // Get the gift message container (try within this component first)
            const giftMessageContainer = this.querySelector('.gift-message-container') ||
                                        document.querySelector('.gift-message-container');

            // Get all dependent checkboxes with context-aware approach
            const suffix = this.context !== 'default' ? `-${this.context}` : '';
            this.dependentCheckboxes = [
                document.getElementById(`giftbag-checkbox-cart${suffix}`),
                document.getElementById(`giftbox-checkbox-cart${suffix}`),
                document.getElementById(`giftbag-checkbox${suffix}`),
                document.getElementById(`giftbox-checkbox${suffix}`),
                // Fallback to original IDs for backward compatibility
                document.querySelector('#giftbag-checkbox-cart'),
                document.querySelector('#giftbox-checkbox-cart'),
                document.querySelector('#giftbag-checkbox'),
                document.querySelector('#giftbox-checkbox'),
            ].filter(Boolean); // Remove null/undefined elements

            // Only proceed if this instance has relevant elements
            if (!giftCheckbox && !this.giftbagCheckbox && !this.giftboxCheckbox) {
                console.log(`No relevant elements found for CartGift context: ${this.context}`);
                return;
            }

            // Set initial state based on localStorage value (context-specific)
            if (giftCheckbox && giftMessageContainer) {
                // Check localStorage for gift status with context
                const storageKey = this.context !== 'default' ? `giftEnabled_${this.context}` : 'giftEnabled';
                const giftEnabledInStorage = localStorage.getItem(storageKey) === 'true';

                // Set checkbox state based on localStorage
                giftCheckbox.checked = giftEnabledInStorage;

                // Initial visibility based on checkbox state
                if (giftCheckbox.checked) {
                    giftMessageContainer.style.display = 'block';
                } else {
                    giftMessageContainer.style.display = 'none';
                    // Initially uncheck all dependent checkboxes if main is unchecked
                    this._uncheckDependentBoxes();
                    // Remove gift items from cart
                    this._removeGiftItemsFromCart();
                }

                // Add event listener for checkbox state change
                giftCheckbox.addEventListener('change', (event) => {
                    if (event.target.checked) {
                        // Show the gift message container when checked
                        giftMessageContainer.style.display = 'block';
                        // Store gift enabled state in localStorage with context
                        localStorage.setItem(storageKey, 'true');
                    } else {
                        // Hide the gift message container when unchecked
                        giftMessageContainer.style.display = 'none';
                        // Uncheck all dependent checkboxes
                        this._uncheckDependentBoxes();
                        // Remove gift items from cart
                        this._removeGiftItemsFromCart();
                        // Remove gift enabled state from localStorage
                        localStorage.removeItem(storageKey);
                    }
                });
            }

            // Initialize all functionality
            this.openCartDrawerTable();
            this.restoreCheckboxStates();
            this.setupCheckboxListeners();
            this.setupPersonalizedMessageForm();
            this.setupObservers();
        }

        openCartDrawerTable() {
            this.checkProductInCart(this.giftBoxProductId).then(isInCart => {
                if (isInCart) {
                    // Gift box is in cart, show personalized message container
                    if (this.personalizedMessageContainer) {
                        this.personalizedMessageContainer.style.display = 'block';
                    }
                } else {
                    // Gift box is not in cart, hide personalized message container
                    if (this.personalizedMessageContainer) {
                        this.personalizedMessageContainer.style.display = 'none';
                    }
                }
            });
        }

        clearMessageInputs() {
            if (this.messageToInput) this.messageToInput.value = '';
            if (this.messageFromInput) this.messageFromInput.value = '';
            if (this.messageTextInput) this.messageTextInput.value = '';
            if (this.messageStatus) this.messageStatus.textContent = '';

            // Clear localStorage with context
            const contextSuffix = this.context !== 'default' ? `_${this.context}` : '';
            localStorage.removeItem(`personalizedMessageTo${contextSuffix}`);
            localStorage.removeItem(`personalizedMessageFrom${contextSuffix}`);
            localStorage.removeItem(`personalizedMessageText${contextSuffix}`);
        }

        // Function to check if a product is in cart
        async checkProductInCart(productId) {
            try {
                const response = await fetch('/cart.js');
                const cart = await response.json();
                return cart.items.some(item => item.variant_id.toString() === productId);
            } catch (error) {
                console.error('Error checking product in cart:', error);
                return false;
            }
        }

        // Comprehensive state restoration function
        async restoreCheckboxStates() {
            try {
                const response = await fetch('/cart.js');
                const cart = await response.json();

                // Check if gift products are actually in the cart
                const giftbagInCart = cart.items.some(item =>
                    item.handle === 'gift-test' || item.variant_id.toString() === this.giftProductId
                );
                const giftboxInCart = cart.items.some(item =>
                    item.handle === 'gift-box' || item.variant_id.toString() === this.giftBoxProductId
                );

                // Create context-specific storage keys
                const contextSuffix = this.context !== 'default' ? `_${this.context}` : '';

                // Force set checkboxes based on cart contents
                if (this.giftbagCheckbox) {
                    this.giftbagCheckbox.checked = giftbagInCart;

                    if (giftbagInCart) {
                        if (this.cartDrawerMessage) {
                            this.cartDrawerMessage.innerHTML = '<span class="already-added" style="color: #666; font-size:10px;">Gift packaging in cart - Untick to remove</span>';
                        }
                        localStorage.setItem(`giftbagChecked${contextSuffix}`, 'true');
                    } else {
                        localStorage.removeItem(`giftbagChecked${contextSuffix}`);
                        if (this.cartDrawerMessage) this.cartDrawerMessage.innerHTML = '';
                    }
                }

                if (this.giftboxCheckbox) {
                    this.giftboxCheckbox.checked = giftboxInCart;

                    if (giftboxInCart) {
                        if (this.giftBoxCartMessage) {
                            this.giftBoxCartMessage.innerHTML = '<span class="already-added" style="color: #666; font-size:10px;">Gift box in cart - Untick to remove</span>';
                        }
                        localStorage.setItem(`giftboxChecked${contextSuffix}`, 'true');
                    } else {
                        localStorage.removeItem(`giftboxChecked${contextSuffix}`);
                        if (this.giftBoxCartMessage) this.giftBoxCartMessage.innerHTML = '';
                    }
                }

            } catch (error) {
                console.error('Error restoring checkbox states:', error);
            }
        }

        // Enhanced event listener for checkboxes
        setupCheckboxListeners() {
            const contextSuffix = this.context !== 'default' ? `_${this.context}` : '';

            if (this.giftbagCheckbox) {
                this.giftbagCheckbox.addEventListener('change', async (event) => {
                    const isChecked = event.target.checked;

                    try {
                        if (isChecked) {
                            // Show loading message
                            if (this.cartDrawerMessage) {
                                this.cartDrawerMessage.innerHTML = '<span style="color: #666; font-size:10px;">Adding gift wrap...</span>';
                            }

                            // Add to cart
                            const response = await fetch('/cart/add.js', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ items: [{ id: this.giftProductId, quantity: 1 }] })
                            });

                            const result = await response.json();
                            this.updateCartCount(1);
                            localStorage.setItem(`giftbagChecked${contextSuffix}`, 'true');
                            if (this.cartDrawerMessage) {
                                this.cartDrawerMessage.innerHTML = '<span class="already-added" style="color: #666; font-size:10px;">Gift packaging in cart - Untick to remove</span>';
                            }

                        } else {
                            // Show loading message
                            if (this.cartDrawerMessage) {
                                this.cartDrawerMessage.innerHTML = '<span style="color: #666; font-size:10px;">Removing gift wrap...</span>';
                            }

                            // Remove from cart
                            const response = await fetch('/cart/update.js', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ updates: { [this.giftProductId]: 0 } })
                            });

                            const result = await response.json();
                            this.updateCartCount(-1);
                            localStorage.removeItem(`giftbagChecked${contextSuffix}`);
                            if (this.cartDrawerMessage) {
                                this.cartDrawerMessage.innerHTML = '';
                            }
                        }
                        this.cartRefresh();
                    } catch (error) {
                        console.error('Error:', error);
                        event.target.checked = !isChecked;
                        if (this.cartDrawerMessage) {
                            this.cartDrawerMessage.innerHTML = '<span style="color: red; font-size:10px;">Error updating cart</span>';
                        }
                    }
                });
            }

            if (this.giftboxCheckbox) {
                this.giftboxCheckbox.addEventListener('change', async (event) => {
                    const isChecked = event.target.checked;

                    try {
                        if (isChecked) {
                            // Show loading message
                            if (this.giftBoxCartMessage) {
                                this.giftBoxCartMessage.innerHTML = '<span style="color: #666; font-size:10px;">Adding gift box...</span>';
                            }

                            // Add to cart
                            const response = await fetch('/cart/add.js', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ items: [{ id: this.giftBoxProductId, quantity: 1 }] })
                            });

                            const result = await response.json();
                            this.updateCartCount(1);

                            localStorage.setItem(`giftboxChecked${contextSuffix}`, 'true');
                            if (this.giftBoxCartMessage) {
                                this.giftBoxCartMessage.innerHTML = '<span class="already-added" style="color: #666; font-size:10px;">Gift box in cart - Untick to remove</span>';
                            }

                            // Show personalized message container immediately
                            if (this.personalizedMessageContainer) {
                                this.personalizedMessageContainer.style.display = 'block';
                            }
                        } else {
                            // Show loading message
                            if (this.giftBoxCartMessage) {
                                this.giftBoxCartMessage.innerHTML = '<span style="color: #666; font-size:10px;">Removing gift box...</span>';
                            }

                            // Clear personalized message inputs with context
                            this.clearMessageInputs();

                            // Remove from cart
                            const response = await fetch('/cart/update.js', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ updates: { [this.giftBoxProductId]: 0 } })
                            });

                            const result = await response.json();
                            this.updateCartCount(-1);
                            localStorage.removeItem(`giftboxChecked${contextSuffix}`);
                            if (this.giftBoxCartMessage) {
                                this.giftBoxCartMessage.innerHTML = '';
                            }

                            // Hide personalized message container immediately
                            if (this.personalizedMessageContainer) {
                                this.personalizedMessageContainer.style.display = 'none';
                            }
                        }
                        this.cartRefresh();
                    } catch (error) {
                        console.error('Error:', error);
                        event.target.checked = !isChecked;
                        if (this.giftBoxCartMessage) {
                            this.giftBoxCartMessage.innerHTML = '<span style="color: red; font-size:10px;">Error updating cart</span>';
                        }
                    }
                });
            }
        }

        // Function to update cart count
        updateCartCount(change) {
            // Update count_counting element
            const countElement = document.getElementById('count_counting');
            if (countElement) {
                let currentCount = parseInt(countElement.textContent) || 0;
                currentCount += change;
                countElement.textContent = currentCount.toString();
            }

            // Update cart-item-count element
            const cartItemCount = document.getElementById('cart-item-count');
            if (cartItemCount) {
                let currentCount = parseInt(cartItemCount.textContent) || 0;
                currentCount += change;
                cartItemCount.textContent = currentCount.toString();
            }
        }

        async cartRefresh() {
            await window.ShopUtils.updateShippingNotices(); //global.js
            await window.ShopUtils.cartRefresh(this.context);
        }

        // Setup personalized message form functionality
        setupPersonalizedMessageForm() {
            if (this.addMessageBtn) {
                // Create context-specific storage keys
                const contextSuffix = this.context !== 'default' ? `_${this.context}` : '';

                // Restore any saved message values with context
                if (localStorage.getItem(`personalizedMessageTo${contextSuffix}`) && this.messageToInput) {
                    this.messageToInput.value = localStorage.getItem(`personalizedMessageTo${contextSuffix}`);
                }
                if (localStorage.getItem(`personalizedMessageFrom${contextSuffix}`) && this.messageFromInput) {
                    this.messageFromInput.value = localStorage.getItem(`personalizedMessageFrom${contextSuffix}`);
                }
                if (localStorage.getItem(`personalizedMessageText${contextSuffix}`) && this.messageTextInput) {
                    this.messageTextInput.value = localStorage.getItem(`personalizedMessageText${contextSuffix}`);
                }

                // Add event listener for the message button
                this.addMessageBtn.addEventListener('click', (event) => this.savePersonalizedMessage(event));
            }
        }

        // Function to save personalized message
        async savePersonalizedMessage(event) {
            // Prevent default form submission or page refresh
            event.preventDefault();

            try {
                const messageTo = this.messageToInput ? this.messageToInput.value.trim() : '';
                const messageFrom = this.messageFromInput ? this.messageFromInput.value.trim() : '';
                const messageText = this.messageTextInput ? this.messageTextInput.value.trim() : '';

                if (!messageTo && !messageFrom && !messageText) {
                    if (this.messageStatus) {
                        this.messageStatus.textContent = 'Please enter at least one message field.';
                        this.messageStatus.style.display = 'block';
                    }
                    return;
                }

                const cartResponse = await fetch('/cart.js');
                const cart = await cartResponse.json();

                // Find only gift box in cart (not gift wrap)
                const giftLineItems = cart.items.filter(item =>
                    item.handle === 'gift-box' ||
                    (this.giftBoxProductId && item.variant_id.toString() === this.giftBoxProductId)
                );

                if (giftLineItems.length === 0) {
                    if (this.messageStatus) {
                        this.messageStatus.textContent = 'Please add a gift box to add a message.';
                        this.messageStatus.style.display = 'block';
                    }
                    return;
                }

                // Update first matching line item
                const targetLineItem = giftLineItems[0];
                const targetLineItemIndex = cart.items.indexOf(targetLineItem) + 1;

                const properties = {};
                if (messageTo) properties['Message To'] = messageTo;
                if (messageFrom) properties['Message From'] = messageFrom;
                if (messageText) properties['Message Text'] = messageText;

                // Show loading status
                if (this.messageStatus) {
                    this.messageStatus.textContent = 'Saving message...';
                    this.messageStatus.style.display = 'block';
                }

                const updateResponse = await fetch('/cart/change.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        line: targetLineItemIndex,
                        properties: properties
                    })
                });

                if (!updateResponse.ok) {
                    throw new Error('Failed to update cart.');
                }

                // Save to localStorage with context
                const contextSuffix = this.context !== 'default' ? `_${this.context}` : '';
                localStorage.setItem(`personalizedMessageTo${contextSuffix}`, messageTo);
                localStorage.setItem(`personalizedMessageFrom${contextSuffix}`, messageFrom);
                localStorage.setItem(`personalizedMessageText${contextSuffix}`, messageText);

                if (this.messageStatus) {
                    this.messageStatus.textContent = 'Message added successfully.';
                    this.messageStatus.style.display = 'block';
                }

            } catch (error) {
                console.error('Error saving personalized message:', error);
                if (this.messageStatus) {
                    this.messageStatus.textContent = 'Failed to save message. Please try again.';
                    this.messageStatus.style.display = 'block';
                }
            }
        }

        // Setup observers for cart changes
        setupObservers() {
            // Add listener to handle potential page transitions
            window.addEventListener('pageshow', (event) => {
                if (event.persisted) {
                    this.restoreCheckboxStates();
                }
            });

            // Setup observer for cart changes
            const cartObserver = new MutationObserver(() => {
                this.restoreCheckboxStates();
                this.openCartDrawerTable();
            });

            const cartHolder = document.querySelector('.cart-holder');

            if (cartHolder) {
                cartObserver.observe(cartHolder, {
                    attributes: true,
                    childList: true,
                    subtree: true
                });
            }
        }

        // Function to uncheck all dependent checkboxes and trigger their events
        _uncheckDependentBoxes() {
            this.dependentCheckboxes.forEach((checkbox) => {
                if (checkbox && checkbox.checked) {
                    // First set checked to false
                    checkbox.checked = false;

                    // Then trigger the change event to activate any associated handlers
                    const changeEvent = new Event('change', {
                        bubbles: true,
                        cancelable: true,
                    });
                    checkbox.dispatchEvent(changeEvent);
                }
            });
        }

        // Function to remove gift box and gift bag items from cart
        _removeGiftItemsFromCart() {
            // Assuming you're using Shopify or similar e-commerce platform
            fetch('/cart.js')
                .then((response) => response.json())
                .then((cart) => {
                    // Filter for gift items using variant IDs
                    const giftItems = cart.items.filter((item) =>
                        item.variant_id == this.giftProductId ||
                        item.variant_id == this.giftBoxProductId
                    );

                    // If gift items exist in cart, remove them
                    if (giftItems.length > 0) {
                        const updates = {};

                        // Mark gift items for removal
                        giftItems.forEach((item) => {
                            updates[item.variant_id] = 0; // Set quantity to 0 to remove
                        });

                        // Update cart with items removed
                        fetch('/cart/update.js', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({ updates: updates }),
                        })
                            .then((response) => response.json())
                            .then((updatedCart) => {
                                console.log('Gift items removed from cart');
                                this.cartRefresh();
                            })
                            .catch((error) => console.error('Error updating cart:', error));
                    }
                })
                .catch((error) => console.error('Error fetching cart:', error));
        }

        // Prevent duplicate initialization
        connectedCallback() {
            if (this.hasAttribute('data-initialized')) {
                return;
            }
            this.setAttribute('data-initialized', 'true');
        }
    }

    if (typeof customElements.get('cart-gift') == 'undefined') {
        customElements.define('cart-gift', CartGift);
    }
}